/**
 * DirectCollectionsPanel.jsx — TREATX MIXED SETTLEMENT (provider surface)
 * =======================================================================
 *
 * Lets an authorised clinic user DECLARE money it collected directly from the
 * patient (cash, bank transfer, clinic POS) and see what the patient still
 * owes. Every amount shown is returned by the backend reconciliation
 * (`GET /api/bookings/provider/{clinic_id}/settlements`) — this component
 * performs NO financial arithmetic of its own.
 *
 * It is deliberately explicit that a declaration is NOT a TreatX receipt and
 * does not settle a TreatX commission invoice.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banknote, RefreshCw, Undo2 } from 'lucide-react';

import { api } from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const fmtMinor = (minor, currency) => {
  if (minor == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency: currency || 'EUR', maximumFractionDigits: 2,
    }).format(Number(minor) / 100);
  } catch {
    return `${(Number(minor) / 100).toFixed(2)} ${currency || ''}`;
  }
};

const METHODS = ['cash', 'bank_transfer', 'clinic_pos', 'other'];

// The backend is the real gate (`order.manage` + clinic scope). This only
// hides an affordance the caller cannot use — e.g. provider_accountant, which
// is read-only here exactly as it is everywhere else.
const canRecord = () => {
  try {
    const perms = JSON.parse(localStorage.getItem('treatx_rbac_permissions') || '[]');
    return Array.isArray(perms) ? perms.includes('order.manage') : false;
  } catch {
    return false;
  }
};

const RecordForm = ({ row, onDone }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    amount: '', method: 'cash',
    collected_at: new Date().toISOString().slice(0, 10),
    reference: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Protects against a double-click on THIS form, not against two genuine
  // receipts: a clinic can legitimately take 500 twice on the same day with
  // the same method, so the key carries a per-form nonce rather than only
  // {contract, date, amount, method}.
  const [nonce] = useState(() => Math.random().toString(36).slice(2, 10));

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/bookings/${row.financial_contract_id}/direct-collections`, {
        amount: parseFloat(form.amount) || 0,
        currency: row.currency,
        method: form.method,
        collected_at: form.collected_at,
        reference: form.reference || null,
        notes: form.notes || null,
        idempotency_key: `${row.financial_contract_id}:${nonce}`,
      });
      onDone();
    } catch (err) {
      const d = err?.response?.data?.detail || err?.response?.data || {};
      setError(d.message || err?.message || t('directCollections.errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-3"
         data-testid={`direct-collection-form-${row.financial_contract_id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <Label className="text-[11px]">{t('directCollections.amount')} ({row.currency})</Label>
          <Input type="number" min="0" step="0.01" value={form.amount}
                 onChange={(e) => setForm({ ...form, amount: e.target.value })}
                 data-testid="direct-collection-amount" />
        </div>
        <div>
          <Label className="text-[11px]">{t('directCollections.method')}</Label>
          <select value={form.method}
                  onChange={(e) => setForm({ ...form, method: e.target.value })}
                  className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-[13px]"
                  data-testid="direct-collection-method">
            {METHODS.map((m) => (
              <option key={m} value={m}>{t(`directCollections.methods.${m}`)}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-[11px]">{t('directCollections.collectedAt')}</Label>
          <Input type="date" value={form.collected_at}
                 onChange={(e) => setForm({ ...form, collected_at: e.target.value })}
                 data-testid="direct-collection-date" />
        </div>
        <div>
          <Label className="text-[11px]">{t('directCollections.reference')}</Label>
          <Input value={form.reference}
                 onChange={(e) => setForm({ ...form, reference: e.target.value })}
                 data-testid="direct-collection-reference" />
        </div>
      </div>
      <div>
        <Label className="text-[11px]">{t('directCollections.notes')}</Label>
        <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
               data-testid="direct-collection-notes" />
      </div>
      <p className="text-[11px] text-slate-500" data-testid="direct-collection-disclaimer">
        {t('directCollections.disclaimer')}
      </p>
      {error && (
        <p className="text-[12px] text-rose-700" data-testid="direct-collection-error">{error}</p>
      )}
      <Button size="sm" disabled={busy || !(parseFloat(form.amount) > 0)} onClick={submit}
              data-testid="direct-collection-submit">
        {busy ? t('common.saving') : t('directCollections.record')}
      </Button>
    </div>
  );
};

const CollectionRows = ({ row, onChanged, writable }) => {
  const { t } = useTranslation();
  const reverse = async (collectionId) => {
    const reason = window.prompt(t('directCollections.reversePrompt'));
    if (!reason) return;
    await api.post(
      `/bookings/${row.financial_contract_id}/direct-collections/${collectionId}/reverse`,
      { reason },
    );
    onChanged();
  };
  if (!row.direct_collections?.length) return null;
  return (
    <table className="w-full text-[12px] mt-2" data-testid={`direct-collection-list-${row.financial_contract_id}`}>
      <tbody>
        {row.direct_collections.map((c) => (
          <tr key={c.collection_id} className={c.status === 'declared' ? 'text-slate-700' : 'text-slate-400 line-through'}>
            <td className="py-1">{c.collected_at}</td>
            <td className="py-1">{t(`directCollections.methods.${c.method}`, { defaultValue: c.method })}</td>
            <td className="py-1 font-mono text-[11px]">{c.reference || '—'}</td>
            <td className="py-1 text-right">{fmtMinor(c.amount_minor, c.currency)}</td>
            <td className="py-1 text-right text-[11px]" data-testid={`direct-collection-status-${c.collection_id}`}>
              {c.status !== 'declared' && t(`directCollections.statuses.${c.status}`, { defaultValue: c.status })}
            </td>
            <td className="py-1 text-right">
              {c.status === 'declared' && writable && (
                <button type="button" onClick={() => reverse(c.collection_id)}
                        className="inline-flex items-center gap-1 text-[11px] underline hover:text-slate-900"
                        data-testid={`direct-collection-reverse-${c.collection_id}`}>
                  <Undo2 className="w-3 h-3" />{t('directCollections.reverse')}
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const DirectCollectionsPanel = ({ clinicId }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [showSettled, setShowSettled] = useState(false);
  const writable = canRecord();

  const load = useCallback(() => {
    if (!clinicId) return;
    setLoading(true); setError(null);
    api.get(`/bookings/provider/${clinicId}/settlements?limit=100`)
      .then((res) => setRows((res?.data || res)?.rows || []))
      .catch((err) => setError(err?.response?.data?.message || err?.message))
      .finally(() => setLoading(false));
  }, [clinicId]);

  useEffect(() => { load(); }, [load]);

  // P1-3 — a booking settled entirely in cash must NOT vanish: its collection
  // history is exactly what a later correction needs. Anything with an open
  // balance, any declaration (live, reversed or voided) or an unresolved
  // reconciliation exception stays on this panel; fully settled rows with no
  // declarations at all are the only ones folded away, behind a toggle.
  const live = useMemo(() => rows.filter((r) => !r.blocked), [rows]);
  const isSettled = (r) => r.patient_outstanding_minor === 0;
  const hasHistory = (r) => (r.direct_collections || []).length > 0;
  const openRows = useMemo(
    () => live.filter((r) => !isSettled(r) || hasHistory(r) || r.reconciliation_exception),
    [live],
  );
  const settledRows = useMemo(
    () => live.filter((r) => isSettled(r) && !hasHistory(r) && !r.reconciliation_exception),
    [live],
  );
  const blocked = useMemo(() => rows.filter((r) => r.blocked), [rows]);

  const BookingRow = ({ r }) => (
    <div className="border border-slate-200 rounded-md p-3"
         data-testid={`direct-collection-booking-${r.financial_contract_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-mono text-[11px] text-slate-600">{r.financial_contract_id}</div>
        <div className="flex flex-wrap gap-4 text-[12px]">
          <span>{t('directCollections.contracted')}: <b>{fmtMinor(r.commissionable_gross_minor, r.currency)}</b></span>
          <span>{t('directCollections.pspCollected')}: <b>{fmtMinor(r.psp_collected_minor, r.currency)}</b></span>
          <span>{t('directCollections.directCollected')}: <b data-testid={`direct-total-${r.financial_contract_id}`}>{fmtMinor(r.direct_collected_minor, r.currency)}</b></span>
          {isSettled(r) ? (
            <span className="text-emerald-700" data-testid={`settled-${r.financial_contract_id}`}>
              {t('directCollections.patientSettled')}
            </span>
          ) : (
            <span className="text-amber-800">{t('directCollections.patientOutstanding')}: <b data-testid={`outstanding-${r.financial_contract_id}`}>{fmtMinor(r.patient_outstanding_minor, r.currency)}</b></span>
          )}
        </div>
        <Button size="sm" variant="outline" disabled={!writable || isSettled(r)}
                onClick={() => setOpenId(openId === r.financial_contract_id ? null : r.financial_contract_id)}
                data-testid={`direct-collection-open-${r.financial_contract_id}`}>
          {t('directCollections.record')}
        </Button>
      </div>
      {r.reconciliation_exception && (
        <p className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1"
           data-testid={`reconciliation-exception-${r.financial_contract_id}`}>
          {t('directCollections.exception')}: {r.reconciliation_exception.message}
        </p>
      )}
      <CollectionRows row={r} onChanged={load} writable={writable} />
      {openId === r.financial_contract_id && (
        <div className="mt-3"><RecordForm row={r} onDone={() => { setOpenId(null); load(); }} /></div>
      )}
    </div>
  );

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-4" data-testid="direct-collections-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[14px] font-semibold text-slate-800 inline-flex items-center gap-2">
          <Banknote className="w-4 h-4" />{t('directCollections.title')}
        </h3>
        <button type="button" onClick={load} className="text-slate-500 hover:text-slate-800"
                data-testid="direct-collections-refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      <p className="text-[12px] text-slate-500 mb-3">{t('directCollections.intro')}</p>

      {loading && <p className="text-[12px] text-slate-500" data-testid="direct-collections-loading">{t('common.loading')}</p>}
      {error && <p className="text-[12px] text-rose-700" data-testid="direct-collections-error">{error}</p>}

      {!loading && !error && openRows.length === 0 && (
        <p className="text-[12px] text-slate-500" data-testid="direct-collections-empty">
          {t('directCollections.empty')}
        </p>
      )}

      <div className="space-y-3">
        {openRows.map((r) => (
          <BookingRow key={r.financial_contract_id} r={r} />
        ))}
      </div>

      {settledRows.length > 0 && (
        <div className="mt-3">
          <button type="button" onClick={() => setShowSettled((v) => !v)}
                  className="text-[12px] text-slate-600 underline"
                  data-testid="direct-collections-toggle-settled">
            {showSettled
              ? t('directCollections.hideSettled')
              : t('directCollections.showSettled', { count: settledRows.length })}
          </button>
          {showSettled && (
            <div className="space-y-3 mt-2" data-testid="direct-collections-settled-list">
              {settledRows.map((r) => (
                <BookingRow key={r.financial_contract_id} r={r} />
              ))}
            </div>
          )}
        </div>
      )}

      {blocked.length > 0 && (
        <p className="text-[11px] text-slate-400 mt-3" data-testid="direct-collections-blocked-note">
          {t('directCollections.blockedNote', { count: blocked.length })}
        </p>
      )}
    </section>
  );
};

export default DirectCollectionsPanel;
